import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { useScreenNavigation } from '../../../../packages/shared/useScreenNavigation';
import { useScreenStateUpdates } from '../../../../packages/shared/useScreenStateUpdates';
import ScreenTitle from '../components/ScreenTitle';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #001122 0%, #003366 100%);
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;


const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 20px;
  height: calc(100% - 100px);
`;

const MetricCard = styled.div`
  background: rgba(0, 20, 40, 0.8);
  border: 1px solid rgba(0, 153, 255, 0.3);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const MetricTitle = styled.h3`
  color: var(--kuutio-blue);
  font-size: 1.2rem;
  margin-bottom: 15px;
  text-align: center;
`;

const MetricValue = styled.div`
  color: white;
  font-size: 2rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
`;

const MetricSubtext = styled.div`
  color: var(--gray);
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: 15px;
`;

function DataScreen({ screenMode = false }) {
  // Handle screen navigation commands (disabled in screenMode - ScreenClient handles navigation)
  useScreenNavigation({ screenMode });
  
  const [data, setData] = useState({
    economicGrowth: 2.1,
    automation: 67,
    migrationIndex: 85,
    politicalPolarization: 42,
    climateAction: 78,
    democraticInnovation: 56,
    euIntegration: 61
  });

  const [timeSeriesData, setTimeSeriesData] = useState([]);

  // Send screen state updates for facilitator preview
  useScreenStateUpdates('data', {
    automation: Math.round(data.automation),
    polarization: Math.round(data.politicalPolarization),
    climateAction: Math.round(data.climateAction),
    innovation: Math.round(data.democraticInnovation)
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setData(prev => ({
        economicGrowth: prev.economicGrowth + (Math.random() - 0.5) * 0.2,
        automation: Math.max(0, Math.min(100, prev.automation + (Math.random() - 0.5) * 5)),
        migrationIndex: Math.max(0, Math.min(100, prev.migrationIndex + (Math.random() - 0.5) * 3)),
        politicalPolarization: Math.max(0, Math.min(100, prev.politicalPolarization + (Math.random() - 0.5) * 4)),
        climateAction: Math.max(0, Math.min(100, prev.climateAction + (Math.random() - 0.5) * 2)),
        democraticInnovation: Math.max(0, Math.min(100, prev.democraticInnovation + (Math.random() - 0.5) * 3)),
        euIntegration: Math.max(0, Math.min(100, prev.euIntegration + (Math.random() - 0.5) * 2))
      }));

      setTimeSeriesData(prev => {
        const newData = [...prev, {
          time: new Date().toLocaleTimeString(),
          polarization: data.politicalPolarization,
          climateAction: data.climateAction,
          innovation: data.democraticInnovation
        }];
        return newData.slice(-10);
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [data]);

  const pieData = [
    { name: 'Baseline', value: 35, color: '#0099ff' },
    { name: 'Transformation', value: 28, color: '#00ff88' },
    { name: 'Fading State', value: 22, color: '#ffcc00' },
    { name: 'Hollowed Out', value: 15, color: '#ff0066' }
  ];

  return (
    <Container>
      <ScreenTitle>
        EUROOPAN DEMOKRATIAN REAALIAIKAISET MITTARIT
      </ScreenTitle>
      
      <GridContainer>
        <MetricCard>
          <MetricTitle>Taloudellinen Kasvu</MetricTitle>
          <MetricValue>{data.economicGrowth.toFixed(1)}%</MetricValue>
          <MetricSubtext>EU:n BKT-kasvu vuositasolla</MetricSubtext>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={timeSeriesData}>
              <Line type="monotone" dataKey="polarization" stroke="#0099ff" strokeWidth={2} dot={false} />
              <XAxis hide />
              <YAxis hide />
            </LineChart>
          </ResponsiveContainer>
        </MetricCard>

        <MetricCard>
          <MetricTitle>Automaatioaste</MetricTitle>
          <MetricValue>{Math.round(data.automation)}%</MetricValue>
          <MetricSubtext>Työpaikoista automatisoitu</MetricSubtext>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120 }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: `conic-gradient(#0099ff 0% ${data.automation}%, rgba(255,255,255,0.1) ${data.automation}% 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold'
            }}>
              {Math.round(data.automation)}%
            </div>
          </div>
        </MetricCard>

        <MetricCard>
          <MetricTitle>Skenaariojakauma</MetricTitle>
          <MetricSubtext>Todennäköisyysarviot 2040</MetricSubtext>
          <ResponsiveContainer width="100%" height={150}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                outerRadius={50}
                fill="#8884d8"
                dataKey="value"
                label={({name, value}) => `${name} ${value}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </MetricCard>

        <MetricCard>
          <MetricTitle>Demokratiaindikaattorit</MetricTitle>
          <ResponsiveContainer width="100%" height={150}>
            <BarChart data={[
              { name: 'Osallistuminen', value: data.democraticInnovation },
              { name: 'Ilmastotoimi', value: data.climateAction },
              { name: 'EU-integraatio', value: data.euIntegration },
              { name: 'Polarisaatio', value: 100 - data.politicalPolarization }
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="name" tick={{ fill: 'white', fontSize: 10 }} />
              <YAxis tick={{ fill: 'white', fontSize: 10 }} />
              <Bar dataKey="value" fill="#0099ff" />
            </BarChart>
          </ResponsiveContainer>
        </MetricCard>
      </GridContainer>
    </Container>
  );
}

export default DataScreen;